/**
 * ThreeCardPoker — BETohalics house table.
 *
 * Direct-from-wallet Ante + Pair Plus + Play mechanics. Server-authoritative
 * game state: /api/tcp/deal → /api/tcp/{id}/play | /api/tcp/{id}/fold.
 * See /app/backend/routes/three_card_poker.py for full rules.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ArrowLeft, HelpCircle, Coins, Sparkles, RotateCcw, TrendingUp, TrendingDown,
    History, Info, Volume2, VolumeX, Hand, Flag, Music, Music2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useCrapsAudio } from '../hooks/useCrapsAudio';
import DealtCard from '../components/blackjack/DealtCard';
import ChipStack from '../components/blackjack/ChipStack';
import HouseCrest from '../components/HouseCrest';
import WinFlash from '../components/WinFlash';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CHIP_DENOMS = [0.5, 1, 5, 25, 100];

// Stagger delay for the initial deal (player card 1 → dealer card 1 → …)
// Kept short so the room feels responsive on mobile — 6 cards × 130 ms =
// ~780 ms total, plenty for the ear/eye + haptic to register per card.
const DEAL_MS = 130;

// ─────────────────────────────────────────────────────────────────────
// SummaryLine — one row of the "hand summary" toast. Shows a bet type,
// color-coded win/lose/push chip, and either the payout (win) or the
// wager (push/fold) or a minus (lose). Isolated so the toast body stays
// legible AND we never accidentally regress the label logic.
// ─────────────────────────────────────────────────────────────────────
const SummaryLine = ({ label, data, isBonus = false }) => {
    if (!data) return null;
    const r = data.result; // 'win' | 'lose' | 'push'
    const chipColor = r === 'win'
        ? 'bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/40'
        : r === 'lose'
            ? 'bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40'
            : 'bg-[#FFD700]/15 text-[#FFD700] border-[#FFD700]/40';
    const chipText = r === 'win' ? 'WIN' : r === 'lose' ? (data.label === 'FOLDED' ? 'FOLD' : 'LOSE') : 'PUSH';
    // Amount rendered:
    // • win: +profit
    // • push: returned wager
    // • lose: -wager
    // • ante_bonus is pure bonus (payout == profit, no wager returned)
    const wager = Number(data.wager || 0);
    const payout = Number(data.payout || 0);
    let amountStr;
    if (r === 'win') {
        const profit = isBonus ? payout : payout - wager;
        amountStr = `+$${profit.toFixed(2)}`;
    } else if (r === 'push') {
        amountStr = `$${wager.toFixed(2)}`;
    } else {
        amountStr = `-$${wager.toFixed(2)}`;
    }
    return (
        <div className="flex items-center gap-2">
            <span className="text-white/70 min-w-[80px]">{label}</span>
            <span className={`text-[9px] font-black tracking-widest px-1.5 py-0.5 rounded border ${chipColor}`}>
                {chipText}
            </span>
            <span className="ml-auto text-white/90 tabular-nums font-bold">
                {amountStr}
            </span>
        </div>
    );
};

/**
 * Three Card Poker felt — one player hand vs dealer, Ante + Play + Pair Plus.
 */
export default function ThreeCardPoker() {
    const nav = useNavigate();
    const { user, refreshUser } = useAuth();

    const [cfg, setCfg] = useState(null);
    const [chip, setChip] = useState(1);
    const [ante, setAnte] = useState(0);
    const [pairPlus, setPairPlus] = useState(0);
    const [game, setGame] = useState(null);
    const [busy, setBusy] = useState(false);
    const [lastBets, setLastBets] = useState(null);
    const [winFlash, setWinFlash] = useState(null);   // current { amount, key, label }
    const [flashQueue, setFlashQueue] = useState([]); // pending flashes (side bets + main queue up here)
    const [rulesOpen, setRulesOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [muted, setMuted] = useState(() => localStorage.getItem('tcp-muted') === '1');
    const [revealed, setRevealed] = useState({ p: 0, d: 0 });
    const [dealerReveal, setDealerReveal] = useState(false); // face-up when Play resolved
    const revealTimersRef = useRef([]);

    const balance = Number(user?.credits || 0) + Number(user?.bonus_credits || 0);

    // Load runtime config (paytables + limits)
    useEffect(() => {
        axios.get(`${API}/tcp/config`, { withCredentials: true })
            .then(({ data }) => setCfg(data))
            .catch(() => toast.error('Could not load Three Card Poker'));
    }, []);

    useEffect(() => { localStorage.setItem('tcp-muted', muted ? '1' : '0'); }, [muted]);

    const audio = useCrapsAudio();
    useEffect(() => { audio.setEffects(!muted); }, [muted, audio]);
    const SFX_MAP = {
        'chip.mp3': 'chip',
        'deal.mp3': 'card_flip',
        'win.mp3': 'win_small',
        'lose.mp3': 'lose',
    };
    const playSound = (name) => audio.play(SFX_MAP[name] || name);

    const clearRevealTimers = () => {
        revealTimersRef.current.forEach((t) => clearTimeout(t));
        revealTimersRef.current = [];
    };
    useEffect(() => () => clearRevealTimers(), []);

    const totalPending = ante + pairPlus;
    const overBalance = totalPending > balance + 0.001;

    // Chip placement handlers
    const addToAnte = () => {
        if (game) return;
        if (!cfg) return;
        const nextVal = Math.round((ante + chip) * 100) / 100;
        if (nextVal > cfg.max_bet) { toast.error(`Max Ante is $${cfg.max_bet}`); return; }
        setAnte(nextVal);
        playSound('chip.mp3');
    };
    const addToPairPlus = () => {
        if (game) return;
        if (!cfg) return;
        const nextVal = Math.round((pairPlus + chip) * 100) / 100;
        if (nextVal > cfg.max_bet) { toast.error(`Max Pair Plus is $${cfg.max_bet}`); return; }
        setPairPlus(nextVal);
        playSound('chip.mp3');
    };
    const clearAnte = () => setAnte(0);
    const clearPairPlus = () => setPairPlus(0);
    const clearAll = () => { setAnte(0); setPairPlus(0); };

    // Deal
    const deal = async (overrideAnte = null, overridePP = null) => {
        const a = overrideAnte ?? ante;
        const pp = overridePP ?? pairPlus;
        if (!cfg) return;
        if (a < cfg.min_bet) { toast.error(`Minimum Ante is $${cfg.min_bet.toFixed(2)}`); return; }
        if (pp > 0 && pp < cfg.min_bet) { toast.error(`Pair Plus must be at least $${cfg.min_bet.toFixed(2)}`); return; }
        if (a + pp > balance + 0.001) { toast.error('Insufficient balance'); return; }
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/tcp/deal`,
                { ante: a, pair_plus: pp },
                { withCredentials: true },
            );
            setGame(data);
            setLastBets({ ante: a, pair_plus: pp });
            if (overrideAnte !== null) setAnte(a);
            if (overridePP !== null) setPairPlus(pp);
            setDealerReveal(false);
            // Trigger staggered reveal — 3 player cards then 3 dealer face-down slots
            setRevealed({ p: 0, d: 0 });
            clearRevealTimers();
            const seq = [
                { k: 'p', n: 1 }, { k: 'd', n: 1 },
                { k: 'p', n: 2 }, { k: 'd', n: 2 },
                { k: 'p', n: 3 }, { k: 'd', n: 3 },
            ];
            seq.forEach((step, idx) => {
                const t = setTimeout(() => {
                    setRevealed((r) => ({ ...r, [step.k]: Math.max(r[step.k], step.n) }));
                    playSound('deal.mp3');
                }, idx * DEAL_MS);
                revealTimersRef.current.push(t);
            });
            refreshUser?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Deal failed');
        } finally { setBusy(false); }
    };

    const doPlay = async () => {
        if (busy || !game) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/tcp/${game.id}/play`, {}, { withCredentials: true });
            setGame(data);
            // Reveal dealer with a small delay for drama
            setTimeout(() => {
                setDealerReveal(true);
                playSound('deal.mp3');
                setTimeout(() => summarize(data), 400);
            }, 150);
            refreshUser?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Play failed');
        } finally { setBusy(false); }
    };

    const doFold = async () => {
        if (busy || !game) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/tcp/${game.id}/fold`, {}, { withCredentials: true });
            setGame(data);
            setTimeout(() => summarize(data), 500);
            refreshUser?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fold failed');
        } finally { setBusy(false); }
    };

    const summarize = (g) => {
        const net = Number(g.net || 0);
        const bd = g.breakdown || {};

        // Compute individual side-bet NET wins so we can pop them each in
        // their own celebration overlay — a Pair Plus or Ante Bonus hit is
        // the whole reason those bets exist, so treat them like main wins.
        const ppWin = bd.pair_plus?.result === 'win';
        const ppNet = ppWin
            ? round2(Number(bd.pair_plus.payout || 0) - Number(bd.pair_plus.wager || 0))
            : 0;
        const abWin = bd.ante_bonus?.result === 'win';
        // Ante Bonus payout is PURE bonus (wager is not returned via this line
        // — the wager comes back through the ANTE line), so payout == net win.
        const abNet = abWin ? round2(Number(bd.ante_bonus.payout || 0)) : 0;
        const mainNet = round2(net - ppNet - abNet);

        // Legacy toasts (kept — they show hand type in text form)
        if (ppWin) toast.success(`🎊 PAIR PLUS · ${bd.pair_plus.label} · +$${bd.pair_plus.payout.toFixed(2)}`, { duration: 6000 });
        if (abWin) toast.success(`💎 ANTE BONUS · ${bd.ante_bonus.label} · +$${bd.ante_bonus.payout.toFixed(2)}`, { duration: 6000 });

        // Per-bet SUMMARY toast — one line per bet type with color-coded
        // win / lose / push chips. Fires on EVERY resolved hand so the truth
        // is always visible, even if the banner label logic ever regresses.
        toast.custom((tId) => (
            <div
                onClick={() => toast.dismiss(tId)}
                className="rounded-xl border border-white/15 bg-black/85 backdrop-blur-md px-3 py-2.5 min-w-[260px] shadow-2xl cursor-pointer"
                data-testid="tcp-hand-summary-toast"
            >
                <div className="text-[10px] uppercase tracking-widest text-white/60 font-black mb-1.5">
                    Hand summary
                </div>
                <div className="space-y-1 text-xs">
                    <SummaryLine label="Ante" data={bd.ante} />
                    <SummaryLine label="Play" data={bd.play} />
                    {bd.pair_plus && <SummaryLine label="Pair Plus" data={bd.pair_plus} />}
                    {bd.ante_bonus && <SummaryLine label="Ante Bonus" data={bd.ante_bonus} isBonus />}
                </div>
                <div className="mt-2 pt-1.5 border-t border-white/10 flex items-center justify-between text-[11px]">
                    <span className="text-white/60 uppercase tracking-widest font-bold">Net</span>
                    <span className={`font-black tabular-nums ${net > 0 ? 'text-[#39FF14]' : net < 0 ? 'text-[#FF3B30]' : 'text-[#FFD700]'}`}>
                        {net > 0 ? '+' : ''}${net.toFixed(2)}
                    </span>
                </div>
            </div>
        ), { duration: 7000, position: 'bottom-right' });

        // Queue full-screen WinFlash pops — side bets first, then main hand.
        const q = [];
        if (ppNet > 0) q.push({ amount: ppNet, key: `tcp-pp-${g.id || Date.now()}`, label: `PAIR PLUS · ${bd.pair_plus.label}` });
        if (abNet > 0) q.push({ amount: abNet, key: `tcp-ab-${g.id || Date.now()}`, label: `ANTE BONUS · ${bd.ante_bonus.label}` });
        if (mainNet > 0) q.push({ amount: mainNet, key: `tcp-main-${g.id || Date.now()}` });

        if (q.length > 0) {
            const [first, ...rest] = q;
            playFlashAudio(first.amount);
            setWinFlash(first);
            setFlashQueue(rest);
            armFlashSafety();
            playSound('win.mp3');
            if (net > 0) toast.success(`🎉 Won +$${net.toFixed(2)}`, { duration: 4500 });
        } else if (net === 0) {
            toast.message('Push — bets returned', { duration: 3000 });
        } else {
            toast.message(`Lost -$${Math.abs(net).toFixed(2)}`, { duration: 3000 });
            playSound('lose.mp3');
        }
    };

    // Round helper — trims tiny FP dust before deciding "did it win?"
    const round2 = (n) => Math.round(Number(n) * 100) / 100;

    // Fire the tier-appropriate audio for a given win amount (per-flash so
    // each side bet + main hand celebration gets its own soundtrack).
    const playFlashAudio = (amount) => {
        if (amount >= 500) audio.play('vault_kaching');
        else if (amount >= 100) audio.play('coin_cascade');
    };

    // Advance the flash queue when the current WinFlash finishes animating
    const handleFlashDone = () => {
        if (flashQueue.length > 0) {
            const [next, ...rest] = flashQueue;
            playFlashAudio(next.amount);
            setWinFlash(next);
            setFlashQueue(rest);
        } else {
            setWinFlash(null);
            clearFlashSafety();
        }
    };

    // Safety net — force-clears any lingering flash after 8s so the overlay
    // can NEVER get stuck. Belt + suspenders on top of the onDone chain.
    const flashSafetyRef = useRef(null);
    const clearFlashSafety = () => {
        if (flashSafetyRef.current) { clearTimeout(flashSafetyRef.current); flashSafetyRef.current = null; }
    };
    const armFlashSafety = () => {
        clearFlashSafety();
        flashSafetyRef.current = setTimeout(() => {
            setWinFlash(null); setFlashQueue([]); flashSafetyRef.current = null;
        }, 8000);
    };
    useEffect(() => () => clearFlashSafety(), []);

    const nextRound = () => {
        setGame(null);
        setDealerReveal(false);
        setRevealed({ p: 0, d: 0 });
    };

    if (!cfg) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#040a12] via-[#020610] to-[#010308] text-white flex items-center justify-center" data-testid="tcp-loading">
                <p className="text-[#8fbea4] text-sm">Loading Three Card Poker…</p>
            </div>
        );
    }
    if (cfg.enabled === false) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#040a12] via-[#020610] to-[#010308] text-white flex flex-col items-center justify-center p-8 text-center" data-testid="tcp-disabled">
                <p className="text-[#FFD700] font-black text-lg mb-2">Three Card Poker is closed</p>
                <p className="text-[#8fbea4] text-sm">The table is on a short break. Please check back soon.</p>
                <Button onClick={() => nav('/lobby')} className="mt-6 bg-[#FFD700] text-black">Back to Casino Floor</Button>
            </div>
        );
    }

    const playerEval = game?.player?.eval;
    const dealerEval = game?.dealer?.eval;
    const bd = game?.breakdown || {};

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#040a12] via-[#020610] to-[#010308] text-white" data-testid="tcp-page">
            <WinFlash win={winFlash} label={winFlash?.label || 'WINNER'} onDone={handleFlashDone} />
            <header className="sticky top-0 z-30 bg-[#040a12]/85 backdrop-blur border-b border-[#0a1e26]">
                <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3 gap-3">
                    <button onClick={() => nav('/lobby')} className="flex items-center gap-1.5 text-[#8fbea4] hover:text-white shrink-0" data-testid="tcp-back-btn">
                        <ArrowLeft className="w-4 h-4" /><span className="text-sm hidden sm:inline">Casino Floor</span>
                    </button>
                    <h1 className="text-base sm:text-xl font-black tracking-wide truncate">
                        <span className="text-[#FFD700]">3</span>
                        <span className="text-[#00E1FF]">-CARD</span>{' '}POKER
                    </h1>
                    <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                        <div
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 border border-[#FFD700]/40 backdrop-blur"
                            data-testid="tcp-wallet-chip"
                            title={`Cash $${Number(user?.credits || 0).toFixed(2)}${Number(user?.bonus_credits || 0) > 0 ? ` · Bonus $${Number(user.bonus_credits).toFixed(2)}` : ''}`}
                        >
                            <Coins className="w-3.5 h-3.5 text-[#FFD700]" />
                            <span className="font-mono text-sm font-black text-[#39FF14] tabular-nums" data-testid="tcp-balance">${balance.toFixed(2)}</span>
                        </div>
                        <button onClick={() => setHistoryOpen(true)} className="p-1.5 rounded-md hover:bg-white/5 text-[#8fbea4]" title="History" data-testid="tcp-history-btn">
                            <History className="w-4 h-4" />
                        </button>
                        <button onClick={() => setMuted((m) => !m)} className="p-1.5 rounded-md hover:bg-white/5" title={muted ? 'Unmute' : 'Mute'} data-testid="tcp-mute-btn">
                            {muted ? <VolumeX className="w-4 h-4 text-[#8fbea4]" /> : <Volume2 className="w-4 h-4 text-[#39FF14]" />}
                        </button>
                        <button onClick={() => audio.setMusic(!audio.musicOn)} className="p-1.5 rounded-md hover:bg-white/5" title={audio.musicOn ? 'Music: on' : 'Music: off'} data-testid="tcp-music-btn">
                            {audio.musicOn
                                ? <Music className="w-4 h-4 text-[#FFD700]" />
                                : <Music2 className="w-4 h-4 text-[#8fbea4]" />}
                        </button>
                        <button onClick={() => setRulesOpen(true)} className="p-1.5 rounded-md hover:bg-white/5" title="How to play" data-testid="tcp-rules-btn">
                            <HelpCircle className="w-4 h-4 text-[#FFD700]" />
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-2 md:px-4 pb-52 md:pb-4">
                <div
                    className="relative rounded-2xl bg-gradient-to-b from-[#0d3d3a] via-[#0a4a44] to-[#062824] border border-[#00E1FF]/25 shadow-[inset_0_0_60px_rgba(0,0,0,0.7),0_0_20px_rgba(0,0,0,0.5)] mt-2 sm:mt-3 overflow-hidden"
                    data-testid="tcp-table"
                >
                    {/* Spotlight + felt weave */}
                    <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
                        backgroundImage: 'radial-gradient(ellipse 60% 40% at 50% 15%, rgba(0,225,255,0.12) 0%, transparent 60%), radial-gradient(circle at 50% 55%, rgba(0,0,0,0.5) 0%, transparent 60%)',
                    }} />
                    <div className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-overlay" style={{
                        backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0 1px, transparent 1px 3px)',
                    }} />

                    {/* Table logo */}
                    <div className="relative pt-2 md:pt-3 pb-1 flex flex-col items-center pointer-events-none z-10 px-2">
                        <HouseCrest size={40} accent="#00E1FF" glow="rgba(0,225,255,0.55)" />
                        <p className="text-[7px] md:text-[10px] tracking-[0.25em] md:tracking-[0.5em] text-[#00E1FF]/80 font-black whitespace-nowrap mt-1">BETOHALICS · 3-CARD POKER</p>
                    </div>

                    {/* ─── FELT-ENGRAVED PAIR PLUS · left rail ────────────────
                        Silk-screened directly onto the felt — no floating card.
                        Cinzel serif + amber glow reads like a real casino table. */}
                    <div
                        className="absolute left-1 md:left-3 top-10 md:top-14 z-[5] pointer-events-none flex flex-col gap-1.5 md:gap-2 max-w-[62px] md:max-w-[128px]"
                        data-testid="tcp-felt-pp-left"
                        aria-label="Pair Plus paytable"
                    >
                        <div className="font-casino felt-engraving text-center text-[9px] md:text-[13px] tracking-[0.24em] border-b border-[#FF007F]/25 pb-0.5" style={{ color: '#FFB6DA', textShadow: '0 0 8px rgba(255,182,218,0.55), 0 0 16px rgba(255,0,127,0.35), 0 1px 0 rgba(0,0,0,0.65)' }}>
                            PAIR PLUS
                        </div>
                        <div className="font-casino flex flex-col gap-[1px] text-[7px] md:text-[10px] leading-[1.2]" style={{ color: '#FFB6DA', textShadow: '0 0 8px rgba(255,182,218,0.5), 0 1px 0 rgba(0,0,0,0.7)' }}>
                            <div className="flex justify-between gap-1.5 md:gap-2"><span>M.Royal</span><span>{cfg.pair_plus.mini_royal}×</span></div>
                            <div className="flex justify-between gap-1.5 md:gap-2"><span>Str Fl</span><span>{cfg.pair_plus.straight_flush}×</span></div>
                            <div className="flex justify-between gap-1.5 md:gap-2"><span>3-Kind</span><span>{cfg.pair_plus.three_of_kind}×</span></div>
                            <div className="flex justify-between gap-1.5 md:gap-2"><span>Str</span><span>{cfg.pair_plus.straight}×</span></div>
                            <div className="flex justify-between gap-1.5 md:gap-2"><span>Flush</span><span>{cfg.pair_plus.flush}×</span></div>
                            <div className="flex justify-between gap-1.5 md:gap-2"><span>Pair</span><span>{cfg.pair_plus.pair}×</span></div>
                        </div>
                    </div>

                    {/* ─── FELT-ENGRAVED ANTE BONUS + HOUSE RULES · right rail ─── */}
                    <div
                        className="absolute right-1 md:right-3 top-10 md:top-14 z-[5] pointer-events-none flex flex-col gap-2 md:gap-3 max-w-[62px] md:max-w-[128px]"
                        data-testid="tcp-felt-rules-right"
                        aria-label="Ante Bonus and house rules"
                    >
                        <div className="font-casino felt-engraving text-center text-[9px] md:text-[13px] tracking-[0.24em] border-b border-[#FFD700]/25 pb-0.5">
                            ANTE BONUS
                        </div>
                        <div className="font-casino felt-engraving flex flex-col gap-[1px] text-[7px] md:text-[10px] leading-[1.2]">
                            <div className="flex justify-between gap-1.5 md:gap-2"><span>Str Fl</span><span>{cfg.ante_bonus.straight_flush}×</span></div>
                            <div className="flex justify-between gap-1.5 md:gap-2"><span>3-Kind</span><span>{cfg.ante_bonus.three_of_kind}×</span></div>
                            <div className="flex justify-between gap-1.5 md:gap-2"><span>Str</span><span>{cfg.ante_bonus.straight}×</span></div>
                        </div>
                        <div className="font-casino felt-engraving text-center text-[7px] md:text-[10px] leading-[1.15] opacity-90 mt-1">
                            <div>DEALER QUALIFIES</div>
                            <div>QUEEN HIGH</div>
                            <div>OR BETTER</div>
                        </div>
                        <div className="font-casino felt-engraving text-center text-[7px] md:text-[10px] leading-[1.15] opacity-85">
                            <div>ANTE PAYS 1 to 1</div>
                            <div>PLAY PAYS 1 to 1</div>
                        </div>
                    </div>

                    {/* ── DEALER SECTION ── */}
                    <section
                        className="relative mx-14 md:mx-32 mt-2 md:mt-4 mb-3 md:mb-4 rounded-2xl border border-[#00E1FF]/30 bg-black/40 backdrop-blur-sm px-2 md:px-3 py-2 md:py-3 shadow-[inset_0_0_20px_rgba(0,0,0,0.4)]"
                        data-testid="tcp-dealer-section"
                    >
                        <div className="absolute -top-2 md:-top-2.5 left-1/2 -translate-x-1/2 px-2 md:px-3 py-0.5 rounded-full bg-[#0a2a2a] border border-[#00E1FF]/40 text-[8px] md:text-[9px] tracking-[0.3em] md:tracking-[0.35em] text-[#00E1FF] font-black">
                            DEALER
                        </div>
                        <div className="flex items-center justify-center gap-1.5 md:gap-2 min-h-[80px] md:min-h-[110px] pt-1" data-testid="tcp-dealer-cards">
                            {game ? (
                                game.dealer.cards.slice(0, revealed.d).map((c, i) => {
                                    const hidden = c === '??';
                                    return (
                                        <DealtCard
                                            key={`d-${i}-${dealerReveal}`}
                                            card={hidden ? null : c}
                                            faceDown={hidden || !dealerReveal}
                                            reveal={!hidden && dealerReveal}
                                        />
                                    );
                                })
                            ) : (
                                <>
                                    <div className="w-12 h-[72px] md:w-[70px] md:h-[100px] rounded-lg border-2 border-dashed border-[#00E1FF]/20" />
                                    <div className="w-12 h-[72px] md:w-[70px] md:h-[100px] rounded-lg border-2 border-dashed border-[#00E1FF]/20" />
                                    <div className="w-12 h-[72px] md:w-[70px] md:h-[100px] rounded-lg border-2 border-dashed border-[#00E1FF]/20" />
                                </>
                            )}
                        </div>
                        {dealerReveal && dealerEval && (
                            <div className="mt-1 flex items-center justify-center gap-2" data-testid="tcp-dealer-eval">
                                <span className={`text-[10px] md:text-xs font-black tracking-widest ${game.dealer.qualified ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                    {dealerEval.label}
                                </span>
                                <span className={`text-[9px] md:text-[10px] font-black px-2 py-0.5 rounded-full ${game.dealer.qualified ? 'bg-[#39FF14]/20 text-[#39FF14]' : 'bg-[#FF3B30]/20 text-[#FF3B30]'}`}>
                                    {game.dealer.qualified ? 'QUALIFIED' : 'NO QUALIFY'}
                                </span>
                            </div>
                        )}
                    </section>

                    {/* ── BET LAYOUT — PAIR+ | ANTE | PLAY ── */}
                    <div className="relative grid grid-cols-3 gap-2 md:gap-6 px-14 md:px-24 pb-3 md:pb-4" data-testid="tcp-bet-row">
                        {/* Pair Plus circle */}
                        <div className="flex flex-col items-center">
                            <span className={`text-[8px] md:text-[10px] font-black tracking-[0.28em] leading-none mb-1 whitespace-nowrap ${pairPlus > 0 || bd.pair_plus?.wager > 0 ? 'text-[#FF007F]' : 'text-[#FF007F]/50'}`}>
                                PAIR +
                            </span>
                            {(() => {
                                const won = bd.pair_plus?.result === 'win';
                                const lost = bd.pair_plus?.result === 'lose';
                                const active = pairPlus > 0 || (game && game.pair_plus > 0);
                                const dispAmt = game ? game.pair_plus : pairPlus;
                                return (
                                    <div
                                        onClick={addToPairPlus}
                                        onKeyDown={(e) => { if (!game && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); addToPairPlus(); } }}
                                        role={!game ? 'button' : undefined}
                                        tabIndex={!game ? 0 : -1}
                                        aria-label="Pair Plus side bet"
                                        aria-disabled={!!game}
                                        className={`w-14 h-14 md:w-20 md:h-20 rounded-full flex items-center justify-center border-2 transition-all relative ${
                                            won
                                                ? 'border-[#FF007F] bg-gradient-to-b from-[#FF007F] to-[#FF61A6] shadow-[0_0_30px_rgba(255,0,127,0.8)] animate-pulse'
                                                : lost
                                                    ? 'border-[#FF3B30]/50 bg-black/30 opacity-60'
                                                    : active
                                                        ? 'border-[#FF007F] bg-gradient-to-b from-[#FF007F]/30 to-[#FF007F]/5 shadow-[0_0_16px_rgba(255,0,127,0.4)]'
                                                        : 'border-dashed border-[#FF007F]/40 bg-black/25 hover:border-[#FF007F]/70'
                                        } ${!game ? 'cursor-pointer hover:scale-105' : 'cursor-default'}`}
                                        data-testid="tcp-pp-circle"
                                    >
                                        {active && !won && !lost && (
                                            <>
                                                <span className="absolute inset-1 rounded-full border border-dashed border-[#FF007F]/60" />
                                                <span className="absolute inset-2 rounded-full border border-[#FF007F]/40" />
                                            </>
                                        )}
                                        {won ? (
                                            <div className="text-center relative z-10 text-black">
                                                <div className="text-[8px] md:text-[10px] font-black leading-none">{bd.pair_plus.label}</div>
                                                <div className="text-[10px] md:text-sm font-black font-mono leading-none mt-0.5">+${bd.pair_plus.payout.toFixed(2)}</div>
                                            </div>
                                        ) : dispAmt > 0 ? (
                                            <div className="text-center relative z-10">
                                                <div className="text-xs md:text-lg font-black text-[#FF007F] leading-tight" data-testid="tcp-pp-amt">${dispAmt.toFixed(2)}</div>
                                                {!game && (
                                                    <button onClick={(e) => { e.stopPropagation(); clearPairPlus(); }} className="text-[8px] md:text-[9px] text-[#8fbea4] hover:text-[#FF3B30] uppercase leading-none" data-testid="tcp-pp-clear">Clear</button>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-[8px] md:text-[10px] text-[#8fbea4] uppercase tracking-widest text-center leading-tight">Optional</span>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Ante circle */}
                        <div className="flex flex-col items-center">
                            <span className={`text-[8px] md:text-[10px] font-black tracking-[0.28em] leading-none mb-1 whitespace-nowrap ${ante > 0 || (game && game.ante > 0) ? 'text-[#FFD700]' : 'text-[#FFD700]/50'}`}>
                                ANTE
                            </span>
                            {(() => {
                                const won = bd.ante?.result === 'win';
                                const push = bd.ante?.result === 'push';
                                const lost = bd.ante?.result === 'lose';
                                const active = ante > 0 || (game && game.ante > 0);
                                const dispAmt = game ? game.ante : ante;
                                return (
                                    <div
                                        onClick={addToAnte}
                                        onKeyDown={(e) => { if (!game && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); addToAnte(); } }}
                                        role={!game ? 'button' : undefined}
                                        tabIndex={!game ? 0 : -1}
                                        aria-label="Ante bet — required"
                                        aria-disabled={!!game}
                                        className={`w-16 h-16 md:w-24 md:h-24 rounded-full flex items-center justify-center border-2 transition-all relative ${
                                            won
                                                ? 'border-[#FFD700] bg-gradient-to-b from-[#FFD700] to-[#FFEB88] shadow-[0_0_35px_rgba(255,215,0,0.9)] animate-pulse'
                                                : push
                                                    ? 'border-[#FFD700]/70 bg-[#FFD700]/10'
                                                    : lost
                                                        ? 'border-[#FF3B30]/50 bg-black/30 opacity-60'
                                                        : active
                                                            ? 'border-[#FFD700] bg-gradient-to-b from-[#FFD700]/30 to-[#FFD700]/5 shadow-[0_0_20px_rgba(255,215,0,0.4)]'
                                                            : 'border-dashed border-[#FFD700]/40 bg-black/25 hover:border-[#FFD700]/70'
                                        } ${!game ? 'cursor-pointer hover:scale-105' : 'cursor-default'}`}
                                        data-testid="tcp-ante-circle"
                                    >
                                        {active && !won && !push && !lost && (
                                            <>
                                                <span className="absolute inset-1.5 rounded-full border-2 border-dashed border-[#FFD700]/40" />
                                                <span className="absolute inset-3 rounded-full border border-[#FFD700]/30" />
                                            </>
                                        )}
                                        {won ? (
                                            <div className="text-center relative z-10 text-black">
                                                <div className="text-[8px] md:text-[10px] font-black leading-none">WIN</div>
                                                <div className="text-[10px] md:text-lg font-black font-mono leading-none mt-0.5">+${bd.ante.payout.toFixed(2)}</div>
                                            </div>
                                        ) : push ? (
                                            <div className="text-center relative z-10">
                                                <div className="text-[8px] md:text-[10px] font-black text-[#FFD700] leading-none">PUSH</div>
                                                <div className="text-[11px] md:text-lg font-black text-[#FFD700] font-mono leading-none mt-0.5">${bd.ante.wager.toFixed(2)}</div>
                                            </div>
                                        ) : dispAmt > 0 ? (
                                            <div className="text-center relative z-10">
                                                <div className="text-sm md:text-2xl font-black text-[#FFD700] leading-tight" data-testid="tcp-ante-amt">${dispAmt.toFixed(2)}</div>
                                                {!game && (
                                                    <button onClick={(e) => { e.stopPropagation(); clearAnte(); }} className="text-[8px] md:text-[9px] text-[#8fbea4] hover:text-[#FF3B30] uppercase leading-none" data-testid="tcp-ante-clear">Clear</button>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-[9px] md:text-xs text-[#FFD700] uppercase font-black tracking-widest text-center leading-tight">TAP</span>
                                        )}
                                    </div>
                                );
                            })()}
                            {bd.ante_bonus?.result === 'win' && (
                                <span className="mt-1 text-[8px] md:text-[10px] font-black text-[#FFD700] bg-[#FFD700]/15 border border-[#FFD700]/40 rounded-full px-2 py-0.5" data-testid="tcp-ante-bonus-badge">
                                    +ANTE BONUS · {bd.ante_bonus.label} · +${bd.ante_bonus.payout.toFixed(2)}
                                </span>
                            )}
                        </div>

                        {/* Play circle */}
                        <div className="flex flex-col items-center">
                            <span className={`text-[8px] md:text-[10px] font-black tracking-[0.28em] leading-none mb-1 whitespace-nowrap ${game?.play > 0 ? 'text-[#39FF14]' : 'text-[#39FF14]/50'}`}>
                                PLAY
                            </span>
                            {(() => {
                                const won = bd.play?.result === 'win';
                                const push = bd.play?.result === 'push';
                                const lost = bd.play?.result === 'lose';
                                const active = game && game.play > 0;
                                const dispAmt = game ? game.play : 0;
                                return (
                                    <div
                                        className={`w-14 h-14 md:w-20 md:h-20 rounded-full flex items-center justify-center border-2 relative ${
                                            won
                                                ? 'border-[#39FF14] bg-gradient-to-b from-[#39FF14] to-[#a3ff8c] shadow-[0_0_35px_rgba(57,255,20,0.7)] animate-pulse'
                                                : push
                                                    ? 'border-[#FFD700]/70 bg-[#FFD700]/10'
                                                    : lost
                                                        ? 'border-[#FF3B30]/50 bg-black/30 opacity-60'
                                                        : active
                                                            ? 'border-[#39FF14] bg-gradient-to-b from-[#39FF14]/30 to-[#39FF14]/5 shadow-[0_0_16px_rgba(57,255,20,0.4)]'
                                                            : 'border-dashed border-[#39FF14]/30 bg-black/25'
                                        }`}
                                        data-testid="tcp-play-circle"
                                    >
                                        {won ? (
                                            <div className="text-center relative z-10 text-black">
                                                <div className="text-[8px] md:text-[10px] font-black leading-none">WIN</div>
                                                <div className="text-[10px] md:text-sm font-black font-mono leading-none mt-0.5">+${bd.play.payout.toFixed(2)}</div>
                                            </div>
                                        ) : push ? (
                                            <div className="text-center relative z-10">
                                                <div className="text-[8px] md:text-[10px] font-black text-[#FFD700] leading-none">PUSH</div>
                                                <div className="text-[10px] md:text-sm font-black text-[#FFD700] font-mono leading-none mt-0.5">${bd.play.wager.toFixed(2)}</div>
                                            </div>
                                        ) : dispAmt > 0 ? (
                                            <div className="text-center relative z-10">
                                                <div className="text-xs md:text-lg font-black text-[#39FF14] leading-tight">${dispAmt.toFixed(2)}</div>
                                            </div>
                                        ) : (
                                            <span className="text-[8px] md:text-[10px] text-[#39FF14]/70 uppercase tracking-widest text-center leading-tight font-black">= ANTE</span>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {/* ── PLAYER SECTION ── */}
                    <section
                        className="relative mx-14 md:mx-32 mt-2 md:mt-2 mb-3 md:mb-4 rounded-2xl border border-[#39FF14]/30 bg-black/40 backdrop-blur-sm px-2 md:px-3 py-2 md:py-3 shadow-[inset_0_0_20px_rgba(0,0,0,0.4)]"
                        data-testid="tcp-player-section"
                    >
                        <div className="absolute -top-2 md:-top-2.5 left-1/2 -translate-x-1/2 px-2 md:px-3 py-0.5 rounded-full bg-[#062824] border border-[#39FF14]/40 text-[8px] md:text-[9px] tracking-[0.3em] md:tracking-[0.35em] text-[#39FF14] font-black">
                            YOUR HAND
                        </div>
                        <div className="flex items-center justify-center gap-1.5 md:gap-2 min-h-[80px] md:min-h-[110px] pt-1" data-testid="tcp-player-cards">
                            {game ? (
                                game.player.cards.slice(0, revealed.p).map((c, i) => (
                                    <DealtCard key={`p-${i}`} card={c} />
                                ))
                            ) : (
                                <>
                                    <div className="w-12 h-[72px] md:w-[70px] md:h-[100px] rounded-lg border-2 border-dashed border-[#39FF14]/20" />
                                    <div className="w-12 h-[72px] md:w-[70px] md:h-[100px] rounded-lg border-2 border-dashed border-[#39FF14]/20" />
                                    <div className="w-12 h-[72px] md:w-[70px] md:h-[100px] rounded-lg border-2 border-dashed border-[#39FF14]/20" />
                                </>
                            )}
                        </div>
                        {playerEval && revealed.p >= 3 && (
                            <div className="mt-1 flex items-center justify-center" data-testid="tcp-player-eval">
                                <span className="text-[10px] md:text-xs font-black tracking-widest text-[#39FF14]">
                                    {playerEval.is_mini_royal ? 'MINI ROYAL' : playerEval.label}
                                </span>
                            </div>
                        )}
                    </section>
                </div>

                {/* ── ACTION BAR ── */}
                <div
                    className="mt-3 md:mt-4 fixed bottom-0 left-0 right-0 md:sticky md:bottom-2 rounded-t-2xl md:rounded-2xl bg-[#020610]/95 backdrop-blur-md border-t border-x md:border md:border-x-[#0a1e26] border-[#00E1FF]/40 md:border-[#00E1FF]/30 p-2.5 md:p-3 shadow-[0_-10px_30px_rgba(0,0,0,0.7)] md:shadow-[0_-8px_20px_rgba(0,0,0,0.5)] z-30 pb-[env(safe-area-inset-bottom,0.75rem)] md:pb-3"
                    data-testid="tcp-action-bar"
                >
                    {!game ? (
                        <>
                            <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                                <p className="text-[10px] sm:text-xs uppercase tracking-wider text-[#8fbea4]">Place your bets</p>
                                {(totalPending > 0) && <button onClick={clearAll} className="text-[10px] sm:text-xs text-[#8fbea4] hover:text-white flex items-center gap-1" data-testid="tcp-clear-all"><RotateCcw className="w-3 h-3" />Clear all</button>}
                            </div>
                            <div className="flex flex-nowrap justify-center gap-1.5 sm:gap-2 mb-2 sm:mb-4 overflow-x-auto" data-testid="tcp-chip-selector">
                                {CHIP_DENOMS.map((v) => (<ChipStack key={v} value={v} selected={chip === v} onClick={() => setChip(v)} />))}
                            </div>
                            <div className="flex items-center justify-between gap-2 sm:gap-3">
                                <p className="text-[11px] sm:text-xs text-[#8fbea4]">Total: <span className="font-mono font-black text-white text-sm sm:text-base">${totalPending.toFixed(2)}</span></p>
                                <Button
                                    onClick={() => deal()}
                                    disabled={busy || ante <= 0 || overBalance}
                                    className="bg-[#FFD700] hover:bg-[#FFEB88] text-black font-black text-sm sm:text-base px-4 sm:px-6 shadow-[0_0_20px_rgba(255,215,0,0.4)]"
                                    data-testid="tcp-deal-btn"
                                >
                                    <Sparkles className="w-4 h-4 mr-1" />
                                    {overBalance ? 'Insufficient' : busy ? 'Dealing…' : 'DEAL'}
                                </Button>
                            </div>
                        </>
                    ) : game.status === 'done' ? (
                        <div className="text-center space-y-3">
                            {(() => {
                                // Banner label is driven by the MAIN game outcome
                                // (ante/play), not the combined net. Side bets
                                // (pair plus + ante bonus) settle independently
                                // and shouldn't turn a winning ante into a "Push".
                                const bd = game.breakdown || {};
                                const anteResult = bd.ante?.result;   // win | lose | push | undefined
                                const playResult = bd.play?.result;   // win | lose | push | null
                                const folded = !!bd.ante && anteResult === 'lose' && bd.ante.label === 'FOLDED';
                                const won = anteResult === 'win' || playResult === 'win';
                                const pushed = !won && (anteResult === 'push' || playResult === 'push') && anteResult !== 'lose';
                                const lost = !won && !pushed;
                                const netAmt = Number(game.net || 0);
                                const showAmount = netAmt !== 0;
                                return (
                                    <p
                                        className={`text-xl font-black ${won ? 'text-[#39FF14]' : lost ? 'text-[#FF3B30]' : 'text-[#FFD700]'}`}
                                        data-testid="tcp-net"
                                    >
                                        {folded ? (
                                            <><TrendingDown className="w-5 h-5 inline mr-1" />Folded · -${Math.abs(netAmt).toFixed(2)}</>
                                        ) : won ? (
                                            <><TrendingUp className="w-5 h-5 inline mr-1" />Win{showAmount ? ` · ${netAmt > 0 ? '+' : ''}$${netAmt.toFixed(2)}` : ''}</>
                                        ) : lost ? (
                                            <><TrendingDown className="w-5 h-5 inline mr-1" />Lose · -${Math.abs(netAmt).toFixed(2)}</>
                                        ) : (
                                            <>Push{showAmount ? ` · ${netAmt > 0 ? '+' : ''}$${netAmt.toFixed(2)}` : ''}</>
                                        )}
                                    </p>
                                );
                            })()}
                            <div className="flex flex-wrap justify-center gap-2">
                                {lastBets && lastBets.ante > 0 && (() => {
                                    const rebetTotal = lastBets.ante + (lastBets.pair_plus || 0);
                                    return (
                                        <Button
                                            onClick={() => {
                                                setGame(null); setDealerReveal(false);
                                                setAnte(lastBets.ante);
                                                setPairPlus(lastBets.pair_plus || 0);
                                                deal(lastBets.ante, lastBets.pair_plus || 0);
                                            }}
                                            disabled={busy || rebetTotal > balance + 0.001}
                                            className="bg-[#39FF14] hover:bg-[#39FF14]/90 text-black font-black px-6"
                                            data-testid="tcp-rebet-btn"
                                        >
                                            <Sparkles className="w-4 h-4 mr-1" />
                                            Rebet ${rebetTotal.toFixed(2)} &amp; Deal
                                        </Button>
                                    );
                                })()}
                                <Button onClick={nextRound} variant="outline" className="border-white/20 text-white font-bold px-5" data-testid="tcp-new-round-btn">
                                    <Coins className="w-4 h-4 mr-1" />New bets
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center space-y-2">
                            <p className="text-[10px] md:text-xs uppercase tracking-wider text-[#8fbea4]">
                                Play (match your Ante) — or fold to forfeit
                            </p>
                            <div className="flex justify-center gap-2 md:gap-3" data-testid="tcp-decision-actions">
                                <Button
                                    onClick={doFold}
                                    disabled={busy || revealed.p < 3}
                                    variant="outline"
                                    className="border-[#FF3B30]/60 text-[#FF3B30] hover:bg-[#FF3B30]/10 font-black px-5"
                                    data-testid="tcp-fold-btn"
                                >
                                    <Flag className="w-4 h-4 mr-1" />FOLD
                                </Button>
                                <Button
                                    onClick={doPlay}
                                    disabled={busy || revealed.p < 3 || game.ante > balance + 0.001}
                                    className="bg-[#39FF14] hover:bg-[#39FF14]/90 text-black font-black px-6 shadow-[0_0_20px_rgba(57,255,20,0.4)]"
                                    data-testid="tcp-play-btn"
                                >
                                    <Hand className="w-4 h-4 mr-1" />PLAY · ${game.ante.toFixed(2)}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <button
                    onClick={() => setRulesOpen(true)}
                    className="mt-4 w-full rounded-2xl bg-[#020610]/60 border border-[#0a1e26] p-3 flex items-center justify-between hover:border-[#00E1FF]/40 transition"
                    data-testid="tcp-rules-card"
                >
                    <div className="flex items-center gap-2">
                        <Info className="w-4 h-4 text-[#00E1FF]" />
                        <span className="text-sm font-bold">Rules & payouts</span>
                        <span className="text-[10px] text-[#8fbea4] hidden sm:inline">Dealer qualifies Q-high · Ante Bonus regardless of dealer · Max ${cfg.max_bet}/bet</span>
                    </div>
                    <span className="text-[#8fbea4] text-xs">Read →</span>
                </button>
            </div>

            <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
                <DialogContent className="bg-[#020610] border-[#0a1e26] text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="tcp-rules-modal">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2"><HelpCircle className="w-5 h-5 text-[#00E1FF]" />How to Play 3-Card Poker</DialogTitle>
                        <DialogDescription className="text-[#8fbea4] text-xs">Rules, hand rankings, and paytables for the BETohalics house table.</DialogDescription>
                    </DialogHeader>
                    <TCPRulesCard cfg={cfg} />
                </DialogContent>
            </Dialog>

            <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
                <DialogContent className="bg-[#020610] border-[#0a1e26] text-white max-w-md" data-testid="tcp-history-modal">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2"><History className="w-5 h-5 text-[#00E1FF]" />Your recent rounds</DialogTitle>
                        <DialogDescription className="text-[#8fbea4] text-xs">Last 15 hands with the final hand rank and net result.</DialogDescription>
                    </DialogHeader>
                    <TCPHistoryList />
                </DialogContent>
            </Dialog>
        </div>
    );
}

const TCPRulesCard = ({ cfg }) => (
    <div className="text-sm text-[#c9e5d4] space-y-3 py-1">
        <p><b className="text-white">Objective:</b> Beat the dealer with a better 3-card hand.</p>
        <div>
            <p className="font-black text-[#FFD700] mb-1">How a hand plays</p>
            <ul className="list-disc pl-5 space-y-0.5 text-xs">
                <li>Place your <span className="text-[#FFD700] font-bold">Ante</span> (required) and optionally <span className="text-[#FF007F] font-bold">Pair Plus</span>.</li>
                <li>You &amp; the dealer each receive 3 cards. Yours face up, dealer&apos;s face down.</li>
                <li>Choose to <span className="text-[#39FF14] font-bold">Play</span> (place a Play bet equal to your Ante) or <span className="text-[#FF3B30] font-bold">Fold</span> (forfeit Ante).</li>
                <li>Dealer needs <b>Queen-high or better</b> to qualify.</li>
                <li>If dealer doesn&apos;t qualify: <b>Ante pays 1:1</b>, Play pushes.</li>
                <li>If dealer qualifies: best hand wins. Ties push.</li>
            </ul>
        </div>
        <div>
            <p className="font-black text-[#FFD700] mb-1">Ante Bonus (any dealer)</p>
            <ul className="list-disc pl-5 space-y-0.5 text-xs font-mono">
                <li>Straight Flush · <b>{cfg.ante_bonus.straight_flush}:1</b></li>
                <li>Three of a Kind · <b>{cfg.ante_bonus.three_of_kind}:1</b></li>
                <li>Straight · <b>{cfg.ante_bonus.straight}:1</b></li>
            </ul>
        </div>
        <div>
            <p className="font-black text-[#FF007F] mb-1">Pair Plus paytable</p>
            <ul className="list-disc pl-5 space-y-0.5 text-xs font-mono">
                <li>Mini Royal (A-K-Q suited) · <b>{cfg.pair_plus.mini_royal}:1</b></li>
                <li>Straight Flush · <b>{cfg.pair_plus.straight_flush}:1</b></li>
                <li>Three of a Kind · <b>{cfg.pair_plus.three_of_kind}:1</b></li>
                <li>Straight · <b>{cfg.pair_plus.straight}:1</b></li>
                <li>Flush · <b>{cfg.pair_plus.flush}:1</b></li>
                <li>Pair · <b>{cfg.pair_plus.pair}:1</b></li>
            </ul>
        </div>
        <p className="text-[11px] text-[#8fbea4] italic">Fresh 52-card deck shuffled cryptographically before every hand. Min ${cfg.min_bet.toFixed(2)} · Max ${cfg.max_bet.toFixed(2)} per bet.</p>
    </div>
);

const TCPHistoryList = () => {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        axios.get(`${API}/tcp/history/mine?limit=15`, { withCredentials: true })
            .then(({ data }) => setRows(data))
            .catch(() => setRows([]));
    }, []);
    if (rows === null) return <p className="text-[#8fbea4] text-sm py-4 text-center">Loading…</p>;
    if (rows.length === 0) return <p className="text-[#8fbea4] text-sm py-4 text-center italic">No rounds yet — play one!</p>;
    return (
        <div className="divide-y divide-white/5">
            {rows.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-2 text-xs" data-testid={`tcp-history-row-${r.id}`}>
                    <div className="flex flex-col">
                        <span className="text-[#8fbea4]">{new Date(r.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                        <span className="text-[10px] text-[#8fbea4]/70">{r.folded ? 'Folded' : r.hand}</span>
                    </div>
                    <span className={`font-mono font-bold ${r.net > 0 ? 'text-[#39FF14]' : r.net < 0 ? 'text-[#FF3B30]' : 'text-[#FFD700]'}`}>
                        {r.net >= 0 ? '+' : ''}${r.net.toFixed(2)}
                    </span>
                </div>
            ))}
        </div>
    );
};
